import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { GeographicInfoCountry } from "@/Common/Models/Localization/GeographicInfoCountry";
import { GeographicInfoState } from "@/Common/Models/Localization/GeographicInfoState";

class LocationMapper {
    private countryLookupsByCountryCode: Map<string, FlatLookupView> = new Map();
    private stateLookupsByCountryCodeAndStateCode: Map<string, Map<string, FlatLookupView>> = new Map();
    private countryCodesByCountryLookupId: Map<string, string> = new Map();
    private stateCodesByStateLookupId: Map<string, string> = new Map();
    private geoCountryByLookupId: Map<string, GeographicInfoCountry> = new Map();
    private geoStateByLookupId: Map<string, GeographicInfoState> = new Map();

    constructor(
        countryCache: GeographicInfoCountry[],
        stateCache: GeographicInfoState[],
        countryAndStateLookups: FlatLookupView[]
    ) {
        this.createCountryAndStateMappings(countryCache, stateCache, countryAndStateLookups);
    }

    public getCountryLookup(twoLetterIsoCountryCode: string): FlatLookupView {
        return this.countryLookupsByCountryCode.get(twoLetterIsoCountryCode);
    }

    public getStateLookup(twoLetterIsoCountryCode: string, twoLetterIsoStateCode: string): FlatLookupView {
        return this.stateLookupsByCountryCodeAndStateCode.get(twoLetterIsoCountryCode).get(twoLetterIsoStateCode);
    }

    public getCountryCode(countryLookupId: string): string {
        return this.countryCodesByCountryLookupId.get(countryLookupId);
    }

    public getStateCode(stateLookupId: string): string {
        return this.stateCodesByStateLookupId.get(stateLookupId);
    }

    public getGeoCountryByLookupId(lookupId: string): GeographicInfoCountry {
        return this.geoCountryByLookupId.get(lookupId);
    }

    public getGeoStateByLookupId(lookupId: string): GeographicInfoState {
        return this.geoStateByLookupId.get(lookupId);
    }

    private createCountryAndStateMappings(
        countryCache: GeographicInfoCountry[],
        stateCache: GeographicInfoState[],
        lookups: FlatLookupView[]
    ): void {
        // get lookups
        const lookupDictionnary = lookups.toDictionary(element => element.LookupCode.concat(element.LookupTypeCode));

        // Create GeographicInfo <-> Lookup mapping and store it in class attribute
        countryCache.forEach(geoCountry => {
            if (!geoCountry.Myle_LookupCode || !geoCountry.Myle_LookupTypeCode)
                return;

            const countryLookup = lookupDictionnary[geoCountry.Myle_LookupCode.concat(geoCountry.Myle_LookupTypeCode)];
            const countryCode = geoCountry.Alpha2Code;
            if (countryLookup && !IsNullOrWhiteSpace(countryCode)) {
                this.countryLookupsByCountryCode.set(countryCode, countryLookup);
                this.countryCodesByCountryLookupId.set(countryLookup.LookupID, countryCode);
                this.geoCountryByLookupId.set(countryLookup.LookupID, geoCountry);
            }
        });

        stateCache.forEach(geoState => {
            if (!geoState.Myle_LookupCode || !geoState.Myle_LookupTypeCode)
                return;

            const stateLookup = lookupDictionnary[geoState.Myle_LookupCode.concat(geoState.Myle_LookupTypeCode)];
            const codes = geoState?.SubdivisionCode?.split("-");
            const countryCode = codes?.[0];
            const stateCode = codes?.[1];
            if (stateLookup && !IsNullOrWhiteSpace(countryCode) && !IsNullOrWhiteSpace(stateCode)) {
                if (!this.stateLookupsByCountryCodeAndStateCode.has(countryCode)) {
                    this.stateLookupsByCountryCodeAndStateCode.set(countryCode, new Map());
                }
                this.stateLookupsByCountryCodeAndStateCode.get(countryCode).set(stateCode, stateLookup);
                this.stateCodesByStateLookupId.set(stateLookup.LookupID, stateCode);
                this.geoStateByLookupId.set(stateLookup.LookupID, geoState);
            }
        });
    }
}

export { LocationMapper };
